import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { links } from "/src/domain/shared/links";
import { isLoggedInByCookie, readConsent, saveConsent } from "./consent";
import { loadAcceptedMetricsForPage, type MetricsPreset } from "./metrics";

const RU_POLICY_URL = `${links.origin}/post/3578/`;
const DEFAULT_POLICY_URL = `${links.origin}/post/3600/`;

type CookieConsentBannerProps = {
  preset: MetricsPreset;
};

function getPolicyUrl() {
  const languages = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  const hasRussianLocale = languages.some((language) =>
    language.toLowerCase().startsWith("ru"),
  );

  return hasRussianLocale ? RU_POLICY_URL : DEFAULT_POLICY_URL;
}

export function CookieConsentBanner({ preset }: CookieConsentBannerProps) {
  const { t } = useTranslation();
  const [isHidden, setIsHidden] = useState(
    () => isLoggedInByCookie() || readConsent() !== null,
  );

  useEffect(() => {
    setIsHidden(isLoggedInByCookie() || readConsent() !== null);
  }, []);

  const acceptNecessaryOnly = () => {
    saveConsent("necessary");
    setIsHidden(true);
  };

  const acceptAllCurrentMetrics = () => {
    saveConsent("accepted");
    setIsHidden(true);
    loadAcceptedMetricsForPage(preset);
  };

  if (isHidden) {
    return null;
  }

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: { xs: 3, sm: 4 },
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 1 } }}>
        <Typography variant="body1" color="text.primary">
          <Trans
            i18nKey="cookieConsentDescription"
            components={{
              policyLink: (
                <Link href={getPolicyUrl()} target="_blank" rel="noreferrer" />
              ),
            }}
          />
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 1, sm: 2 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="center"
          width="100%"
          sx={{
            "& > button": {
              flex: { xs: "none", sm: 1 },
            },
          }}
        >
          <Button
            type="button"
            variant="contained"
            onClick={acceptAllCurrentMetrics}
            size="large"
            sx={{ textTransform: "none" }}
          >
            {t("cookieConsentAcceptAnalytics")}
          </Button>

          <Button
            type="button"
            variant="outlined"
            onClick={acceptNecessaryOnly}
            size="large"
            sx={{ textTransform: "none" }}
          >
            {t("cookieConsentNecessaryOnly")}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
